/**
 * Página de Calendario Unificado CRM
 * @module pages/CRMCalendar
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
} from 'date-fns';
import { PlusIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import { CalendarView, type CalendarViewMode } from '@/components/appointments/CalendarView';
import { CalendarFilters } from '@/components/appointments/CalendarFilters';
import { AppointmentForm } from '@/components/appointments/AppointmentForm';
import { ICalExportButton } from '@/components/appointments/ICalExportButton';
import { ICalImportButton } from '@/components/appointments/ICalImportButton';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/common/Modal';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useUnifiedCalendar } from '@/hooks/useUnifiedCalendar';
import { useAppointments } from '@/hooks/useAppointments';
import { useCRMUsers } from '@/hooks/useCRMUsers';
import type { CalendarEvent, CalendarEventType } from '@/types/calendar.types';
import type { AppointmentCreate } from '@/types/appointment.types';

const WEEK_STARTS_ON = 1; // Lunes

function formatDateForUrl(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDateFromUrl(dateStr: string | null): Date | null {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
}

export function CRMCalendar() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlDate = parseDateFromUrl(searchParams.get('date'));
  const urlViewParam = searchParams.get('view');
  const urlView: CalendarViewMode =
    urlViewParam === 'week' ? 'week' : urlViewParam === 'day' ? 'day' : 'month';
  /** Fecha de referencia del periodo mostrado (inicio de semana o de mes) */
  const [periodStart, setPeriodStart] = useState(() => urlDate ?? new Date());
  const [viewMode, setViewMode] = useState<CalendarViewMode>(() => urlView);

  // Sincronizar estado con URL al cambiar (navegación atrás/adelante)
  useEffect(() => {
    const d = parseDateFromUrl(searchParams.get('date'));
    const vp = searchParams.get('view');
    const v: CalendarViewMode = vp === 'week' ? 'week' : vp === 'day' ? 'day' : 'month';
    if (d) setPeriodStart(d);
    setViewMode(v);
  }, [searchParams]);

  // Escribir en la URL cuando cambie periodo o vista (evitar loop: solo después de primera sync)
  const updateUrl = (date: Date, view: CalendarViewMode) => {
    const params = new URLSearchParams(searchParams);
    params.set('date', formatDateForUrl(date));
    params.set('view', view);
    setSearchParams(params, { replace: true });
  };
  const [selectedDelegation, setSelectedDelegation] = useState<string | undefined>();
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
  const [selectedEventTypes, setSelectedEventTypes] = useState<CalendarEventType[]>([
    'appointment',
    'call',
    'task',
  ]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const { create: createAppointment } = useAppointments();

  // Cargar usuarios para mostrar avatares con iniciales en los eventos
  const { users: crmUsers } = useCRMUsers({ isActive: true, onlyResponsibles: true });

  const { start_date, end_date } = useMemo(() => {
    if (viewMode === 'day') {
      return {
        start_date: startOfDay(periodStart).toISOString(),
        end_date: endOfDay(periodStart).toISOString(),
      };
    }
    if (viewMode === 'week') {
      const start = startOfWeek(periodStart, { weekStartsOn: WEEK_STARTS_ON });
      const end = endOfWeek(periodStart, { weekStartsOn: WEEK_STARTS_ON });
      return {
        start_date: start.toISOString(),
        end_date: end.toISOString(),
      };
    }
    return {
      start_date: startOfMonth(periodStart).toISOString(),
      end_date: endOfMonth(periodStart).toISOString(),
    };
  }, [periodStart, viewMode]);

  const { data: events, isLoading, error } = useUnifiedCalendar({
    start_date,
    end_date,
    delegation_id: selectedDelegation,
    user_id: selectedUserId,
  });

  // Mapa userId → nombre para enriquecer eventos con userName
  const userNameMap = useMemo(() => {
    const map = new Map<string, string>();
    crmUsers.forEach((u) => map.set(u.id, u.name));
    return map;
  }, [crmUsers]);

  // Filter events by type y enriquecer con userName
  const filteredEvents = useMemo(() => {
    const filtered = events?.filter((event) => selectedEventTypes.includes(event.type)) || [];
    return filtered.map((event) => ({
      ...event,
      userName: event.userName || (event.userId ? userNameMap.get(event.userId) : null) || null,
    }));
  }, [events, selectedEventTypes, userNameMap]);

  const handleEventClick = (event: CalendarEvent) => {
    // Navegar según el tipo de evento
    switch (event.type) {
      case 'appointment':
        navigate(`/crm/appointments/${event.id}`);
        break;
      case 'call':
        navigate(`/crm/contacts/${event.contactId}`);
        break;
      case 'task':
        navigate(`/crm/tasks/${event.id}`);
        break;
    }
  };

  const handleDateClick = (date: Date) => {
    // Al hacer clic en un día del grid → navegar a vista diaria de ese día
    setPeriodStart(date);
    setViewMode('day');
    updateUrl(date, 'day');
  };

  const handleNavigate = (delta: number) => {
    let next: Date;
    if (viewMode === 'day') {
      next = delta > 0 ? addDays(periodStart, delta) : subDays(periodStart, -delta);
    } else if (viewMode === 'week') {
      next = delta > 0 ? addWeeks(periodStart, delta) : subWeeks(periodStart, -delta);
    } else {
      next = delta > 0 ? addMonths(periodStart, delta) : subMonths(periodStart, -delta);
    }
    setPeriodStart(next);
    updateUrl(next, viewMode);
  };

  const handleGoToToday = () => {
    const today = new Date();
    setPeriodStart(today);
    setViewMode('day');
    updateUrl(today, 'day');
  };

  const handleCreateAppointment = async (data: AppointmentCreate) => {
    try {
      await createAppointment(data);
      setShowCreateModal(false);
      setSelectedDate(null);
    } catch (error) {
      console.error('Error creando cita:', error);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Calendario CRM</h1>
            <p className="text-sm text-gray-600 mt-1">
              Vista unificada de citas, llamadas y tareas
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => navigate('/crm/appointments')}
              variant="outline"
            >
              <ListBulletIcon className="w-4 h-4 mr-2" />
              Ver Lista
            </Button>
            <Button onClick={() => setShowCreateModal(true)}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Nueva Cita
            </Button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
        <CalendarFilters
          selectedDelegation={selectedDelegation}
          onDelegationChange={setSelectedDelegation}
          selectedEventTypes={selectedEventTypes}
          onEventTypesChange={setSelectedEventTypes}
          selectedUserId={selectedUserId}
          onUserChange={setSelectedUserId}
        />
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 p-0.5">
            <Button
              variant={viewMode === 'day' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => {
                setViewMode('day');
                updateUrl(periodStart, 'day');
              }}
            >
              Día
            </Button>
            <Button
              variant={viewMode === 'week' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => {
                setViewMode('week');
                updateUrl(periodStart, 'week');
              }}
            >
              Semana
            </Button>
            <Button
              variant={viewMode === 'month' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => {
                setViewMode('month');
                updateUrl(periodStart, 'month');
              }}
            >
              Mes
            </Button>
          </div>
          <ICalExportButton
            filters={{ delegation_id: selectedDelegation }}
            variant="outline"
            size="sm"
          />
          <ICalImportButton
            params={{ delegation_id: selectedDelegation }}
            variant="outline"
            size="sm"
          />
        </div>
      </div>

      {/* Calendario */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800 font-semibold mb-2">
            ⚠️ Error al cargar el calendario
          </p>
          <p className="text-red-600 text-sm">
            {error instanceof Error ? error.message : 'Error desconocido'}
          </p>
          <p className="text-red-600 text-xs mt-2">
            Verifica que los endpoints del backend estén implementados.
          </p>
        </div>
      ) : (
        <CalendarView
          events={filteredEvents}
          periodStart={periodStart}
          viewMode={viewMode}
          onNavigate={handleNavigate}
          onGoToToday={handleGoToToday}
          onEventClick={handleEventClick}
          onDateClick={handleDateClick}
        />
      )}

      {/* Modal de crear cita */}
      {showCreateModal && (
        <Modal
          open={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedDate(null);
          }}
          title="Nueva Cita"
        >
          <AppointmentForm
            onSubmit={handleCreateAppointment}
            onCancel={() => {
              setShowCreateModal(false);
              setSelectedDate(null);
            }}
            defaultValues={
              selectedDate
                ? {
                    scheduled_at: selectedDate.toISOString().slice(0, 16),
                  }
                : undefined
            }
          />
        </Modal>
      )}
    </div>
  );
}
